import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { clientsService, jobsService, servicesService, staffService } from '../../services/api';
import { JOB_STATUSES } from '../../utils/jobs';
import { endTimeFor, addMinutesToTime, minutesBetween, spanDates, describeSpan, spanResultLabel, fmtDayYmd, DEFAULT_DAY_MINUTES } from '../../utils/schedule';
import './JobModal.css';

const EMPTY = { customer_id: '', title: '', status: 'enquiry', notes: '' };
const NO_SCHED = { service_id: '', start_date: '', start_time: '08:00', end_date: '', end_time: '', skip_weekends: true, ignore_hours: false, staff_ids: [] };

/**
 * Create a job by hand (a client rang, or a quote was accepted and the work
 * needs booking) or edit a job's details. A new job can be scheduled as it is
 * created: a start date and time to an end date and time books one visit per
 * working day, each day blocked start → end, so the calendar holds the whole
 * job (GID-Session-012). Bookings and quotes create their jobs automatically.
 */
export default function JobModal({ isOpen, onClose, onSave, job = null, prefill = null }) {
  const isEdit = !!job;
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [sched, setSched] = useState(NO_SCHED);
  const [results, setResults] = useState(null); // { job, results } when the job was made but some days did not book
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setResults(null);
    clientsService.getAll().then((r) => { if (r.success) setClients(r.customers || []); });
    if (!isEdit) {
      servicesService.getAll().then((r) => { if (r.success) setServices((r.services || []).filter((x) => x.is_active)); });
      staffService.getAll().then((r) => { if (r.success) setStaff((r.staff || []).filter((p) => p.is_active)); });
    }
    setForm(job
      ? { customer_id: job.customer_id ? String(job.customer_id) : '', title: job.title || '', status: job.status || 'enquiry', notes: job.notes || '' }
      : { ...EMPTY, customer_id: prefill?.customer_id ? String(prefill.customer_id) : '', title: prefill?.title || '' });
    setSched(NO_SCHED);
  }, [isOpen, job, prefill, isEdit]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const service = services.find((x) => String(x.id) === sched.service_id);
  const setS = (k, v) => setSched((p) => {
    const next = { ...p, [k]: v };
    if (k === 'start_date') {
      if (!p.end_date || p.end_date < v) next.end_date = v; // the end follows the start until it is set
      if (!p.end_time) next.end_time = endTimeFor(service, p.start_time);
    }
    if (k === 'start_time') next.end_time = addMinutesToTime(v, minutesBetween(p.start_time, p.end_time) || service?.duration_minutes || DEFAULT_DAY_MINUTES); // keep the day's length
    if (k === 'service_id') {
      const svc = services.find((x) => String(x.id) === v);
      next.staff_ids = svc?.staff_ids || [];
      next.end_time = endTimeFor(svc, p.start_time);
    }
    return next;
  });
  const toggleStaff = (id) => setSched((p) => ({ ...p, staff_ids: p.staff_ids.includes(id) ? p.staff_ids.filter((x) => x !== id) : [...p.staff_ids, id] }));
  const scheduling = !isEdit && !!sched.start_date;
  const days = scheduling ? spanDates(sched.start_date, sched.end_date || sched.start_date, sched.skip_weekends).length : 0;

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_id) { setError('Choose a client'); return; }
    let schedule = null;
    if (scheduling) {
      if (!sched.service_id) { setError('Pick a job type to schedule, or clear the start date'); return; }
      if (!sched.start_time || !sched.end_time) { setError('Pick a start time and an end time'); return; }
      if ((minutesBetween(sched.start_time, sched.end_time) || 0) <= 0) { setError('The end time has to be after the start time'); return; }
      if (sched.end_date && sched.end_date < sched.start_date) { setError('The end date has to be on or after the start date'); return; }
      if (!days) { setError('Every day in that span is a weekend — untick "Skip weekends" or pick other days'); return; }
      if (!sched.staff_ids.length) { setError('Pick who is on it'); return; }
      schedule = { service_id: Number(sched.service_id), start_date: sched.start_date, start_time: sched.start_time, end_date: sched.end_date || sched.start_date, end_time: sched.end_time, skip_weekends: sched.skip_weekends, ignore_hours: sched.ignore_hours, staff_ids: sched.staff_ids };
    }
    setSaving(true);
    setError('');
    const payload = { customer_id: Number(form.customer_id), title: form.title.trim(), status: form.status, notes: form.notes, ...(schedule ? { schedule } : {}) };
    const r = isEdit ? await jobsService.update(job.id, payload) : await jobsService.create(payload);
    setSaving(false);
    if (!r.success) { setError(r.error || 'Could not save the job'); return; }
    if (r.schedule && r.schedule.results.some((x) => !x.ok)) { setResults({ job: r.job, results: r.schedule.results }); return; } // the job exists — say which days did not book
    onSave?.(r.job);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? `Edit ${job.job_number}` : 'New Job'} size={isEdit ? 'medium' : 'large'}>
      <form onSubmit={submit}>
        <fieldset className="jm-fields" disabled={!!results}>
          <div className="form-group">
            <label htmlFor="job_title">Title</label>
            <input id="job_title" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Full outdoor pressure wash" autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="job_client">Client *</label>
              <select id="job_client" value={form.customer_id} onChange={(e) => set('customer_id', e.target.value)}>
                <option value="">Select…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.address_suburb ? ` — ${c.address_suburb}` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="job_status">Status</label>
              <select id="job_status" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="job_notes">Notes (internal)</label>
            <textarea id="job_notes" rows="3" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Access, gate code, what the client asked for, anything the crew should know…" />
          </div>

          {!isEdit && (
            <div className="jm-sched">
              <p className="jm-sched-title">Schedule</p>
              <p className="text-muted jm-sched-hint">Set a start and an end to book the job now — one visit per working day, each day blocked start to end. Leave the start date blank to schedule later from the job.</p>
              <div className="jm-sched-row">
                <label>Job type
                  <select value={sched.service_id} onChange={(e) => setS('service_id', e.target.value)}>
                    <option value="">Select…</option>
                    {services.map((x) => <option key={x.id} value={x.id}>{x.name}{x.duration_minutes ? ` (${x.duration_minutes} min)` : ''}</option>)}
                  </select>
                </label>
              </div>
              <div className="jm-sched-row jm-sched-when">
                <label>Start date<input type="date" value={sched.start_date} onChange={(e) => setS('start_date', e.target.value)} /></label>
                <label>Start time<input type="time" value={sched.start_time} onChange={(e) => setS('start_time', e.target.value)} /></label>
                <label>End date<input type="date" min={sched.start_date || undefined} value={sched.end_date} onChange={(e) => setS('end_date', e.target.value)} /></label>
                <label>End time<input type="time" value={sched.end_time} onChange={(e) => setS('end_time', e.target.value)} /></label>
              </div>
              {scheduling && (
                <>
                  <div className="jm-sched-row jm-sched-crew">
                    <span className="text-muted">Who's on it</span>
                    {staff.map((p) => (
                      <label key={p.id} className={`crew-option ${sched.staff_ids.includes(p.id) ? 'on' : ''}`}>
                        <input type="checkbox" checked={sched.staff_ids.includes(p.id)} onChange={() => toggleStaff(p.id)} />
                        <span className="crew-dot" style={{ background: p.color || 'var(--color-teal)' }} />
                        {p.name}
                      </label>
                    ))}
                    <label className="checkbox-label jm-sched-tick">
                      <input type="checkbox" checked={sched.skip_weekends} onChange={(e) => setS('skip_weekends', e.target.checked)} /> Skip weekends
                    </label>
                    <label className="checkbox-label jm-sched-tick" title="Book even when the crew's roster (Availability) does not cover the time. Clashes with other visits still count.">
                      <input type="checkbox" checked={sched.ignore_hours} onChange={(e) => setS('ignore_hours', e.target.checked)} /> Outside working hours is OK
                    </label>
                  </div>
                  <p className="text-muted jm-sched-summary">{describeSpan(sched)}. Each day is checked against availability. No emails go out.</p>
                </>
              )}
            </div>
          )}
        </fieldset>

        {results && (
          <div className="jm-results">
            <p><strong>{results.job.job_number} is created</strong>, but not every day could be booked:</p>
            <ul>
              {results.results.map((x) => <li key={x.date} className={x.ok ? 'ok' : 'bad'}>{fmtDayYmd(x.date)}: {spanResultLabel(x)}</li>)}
            </ul>
            {!sched.ignore_hours && results.results.some((x) => x.code === 'OUTSIDE_HOURS') && (
              <p className="text-muted">Open the job, use + Schedule and tick "Outside working hours is OK" to book those days anyway — or widen the roster under Availability.</p>
            )}
          </div>
        )}

        {error && <div className="form-error text-center mb-md">{error}</div>}
        <div className="modal-actions">
          {results ? (
            <button type="button" className="btn btn-primary" onClick={() => { onSave?.(results.job); onClose(); }}>Open the job</button>
          ) : (
            <>
              <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save' : scheduling ? (days > 1 ? `Create job + book ${days} days` : 'Create job + book the visit') : 'Create job'}
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}
